import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FlatList, Text, View } from 'react-native'
import {
  BG_STYLES,
  COMMON_STYLES,
  TEXT_STYLES,
  TYPOGRAPHY,
  Spinner,
} from '@dehaat/dds'
import { useDispatch, useSelector } from 'react-redux'

import fetchUserRole from 'app/apis/fetchUserRole'
import fetchUserDetails from 'app/apis/fetchUserDetails'
import useDemoMaterialRequest from 'app/hooks/useDemoMaterialRequest'
import ToastAndroid from 'app/mocks/ToastAndroid'
import FilterModal from 'app/components/priceRequest/FilterModal'
import RoleSelection from 'app/components/priceRequest/RoleSelection'
import {
  ROLE_TYPES,
  selectRoleType,
  setUserRole,
} from 'app/store/reducer/requestflow'
import { RequestAction, RoleType, STATUS } from 'app/constants/request'
import { DemoMaterialRequest } from 'app/models/RequestFlow'
import { ResponsiveContext } from 'app/context/ResponsiveUIContext'
import { Button, BUTTON_TYPE, SIZE } from '@dehaat/dds'
import { rnNavigation } from 'app/utils/appConfig'
import { ROUTES } from 'app/routes/types'
import validateDemoMaterialRequest, {
  DemoMaterialActionResult,
} from 'app/apis/validateDemoMaterialRequest'
import withdrawDemoMaterialRequest from 'app/apis/withdrawDemoMaterialRequest'
import DemoMaterialRequestCard from './DemoMaterialRequestCard'
import DemoMaterialRequestFilterHeader from './DemoMaterialRequestFilterHeader'
import DemoMaterialRequestDetailSheet from './DemoMaterialRequestDetailSheet'
import DemoMaterialRequestActionsPanel from './DemoMaterialRequestActionsPanel'
import styles from './homeDemoMaterialRequest.styles'
import { useTranslation } from 'react-i18next'

const STATUS_FILTERS = ['Pending', 'Approved', 'Rejected']

type AppliedFilters = {
  created_at_after?: string
  created_at_before?: string
  status?: string
  request_type?: string
  [key: string]: any
}

interface HomeDemoMaterialRequestProps {
  resetFilters?: boolean
}

const HomeDemoMaterialRequest = ({
  resetFilters,
}: HomeDemoMaterialRequestProps) => {
  const dispatch = useDispatch()
  const roleType = useSelector(selectRoleType)
  const { mediaStyles } = useContext(ResponsiveContext)
  const { t } = useTranslation()

  const [showFilterModal, setShowFilterModal] = useState(false)
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({})
  const [statusFilter, setStatusFilter] = useState<string>(STATUS_FILTERS[0])
  const [showRoleSelection, setShowRoleSelection] = useState(false)
  const [availableRoles, setAvailableRoles] = useState<any[]>([])
  const [showNoRole, setNoRole] = useState(false)
  const [roleInitialized, setRoleInitialized] = useState(false)
  const [roleLoading, setRoleLoading] = useState(true)
  const [selectedRequest, setSelectedRequest] = useState<DemoMaterialRequest | null>(null)
  const [showDetailsSheet, setShowDetailsSheet] = useState(false)
  const [selectedRequestIds, setSelectedRequestIds] = useState<number[]>([])
  const [isMultiSelectEnabled, setIsMultiSelectEnabled] = useState(false)
  const [showRejectDrawer, setShowRejectDrawer] = useState(false)
  const [otherReason, setOtherReason] = useState('')
  const [rejectValidationError, setRejectValidationError] = useState('')
  const [showListResultPopup, setShowListResultPopup] = useState(false)
  const [listResultMessage, setListResultMessage] = useState('')
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [pendingAction, setPendingAction] = useState<RequestAction | null>(null)
  const [isDetailAction, setIsDetailAction] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [currentUserId, setCurrentUserId] = useState<number | null>(null)

  const { fetchNext, request, loading, loadingNext, getRequest } =
    useDemoMaterialRequest({
      request_type: 'DEMO_MATERIAL_ISSUANCE',
      status: appliedFilters.status || statusFilter,
      roleType: roleType,
      created_at_after: appliedFilters.created_at_after,
      created_at_before: appliedFilters.created_at_before,
      enabled: roleInitialized,
    })

  useEffect(() => {
    if (resetFilters) {
      setAppliedFilters({})
      setStatusFilter('Pending')
      setSelectedRequestIds([])
      setIsMultiSelectEnabled(false)
    }
  }, [resetFilters])

  const isRoleType = (value: string): value is RoleType => {
    return ROLE_TYPES.includes(value as RoleType)
  }

  useEffect(() => {
    const initializeUserRole = async () => {
      try {
        const savedRole = localStorage.getItem('UserRole')
        if (savedRole && isRoleType(savedRole)) {
          dispatch(setUserRole(savedRole))
          setRoleLoading(false)
          setRoleInitialized(true)
          return
        }

        setRoleLoading(true)
        const apiResponse = await fetchUserRole()
        if (!apiResponse) {
          ToastAndroid.show(t('unknown_error'), ToastAndroid.SHORT)
          setRoleLoading(false)
          setRoleInitialized(true)
          return
        }

        if (apiResponse.role_type.length === 0) {
          setNoRole(true)
        } else if (apiResponse.role_type.length > 1) {
          setAvailableRoles(apiResponse.role_type)
          setShowRoleSelection(true)
        } else if (apiResponse.role_type.length === 1) {
          const singleRole = apiResponse.role_type[0]
          dispatch(setUserRole(singleRole))
          setRoleInitialized(true)
        }
      } catch {
        ToastAndroid.show(t('unknown_error'), ToastAndroid.SHORT)
      } finally {
        setRoleLoading(false)
      }
    }

    initializeUserRole()
  }, [dispatch, t])

  useEffect(() => {
    const getCurrentUser = async () => {
      const user = await fetchUserDetails()
      if (user?.id) {
        setCurrentUserId(user.id)
      }
    }
    getCurrentUser()
  }, [])

  const handleRoleSelect = (selectedRole: any) => {
    dispatch(setUserRole(selectedRole))
    localStorage.setItem('UserRole', selectedRole)
    setShowRoleSelection(false)
    setRoleInitialized(true)
  }

  const handleApplyFilters = (filters: any) => {
    setAppliedFilters(filters)
    const matched = STATUS_FILTERS.find(
      s => s.toLowerCase() === String(filters.status).toLowerCase(),
    )
    if (matched) {
      setStatusFilter(matched)
    } else {
      setStatusFilter('Pending')
    }
    setShowFilterModal(false)
    setSelectedRequestIds([])
    setIsMultiSelectEnabled(false)
  }

  const onChangeStatus = (status: string) => {
    setStatusFilter(status)
    const mappedStatus = status.toUpperCase()
    setAppliedFilters(prev => ({
      ...prev,
      status:
        mappedStatus === STATUS.PENDING
          ? STATUS.PENDING
          : mappedStatus === STATUS.APPROVED
          ? STATUS.APPROVED
          : STATUS.REJECTED,
    }))
    setSelectedRequestIds([])
    setIsMultiSelectEnabled(false)
  }

  const handleRemoveFilter = (key: string) => {
    setAppliedFilters(prev => {
      const next: AppliedFilters = { ...prev }
      if (key === 'date_range') {
        delete next.created_at_after
        delete next.created_at_before
      } else {
        delete next[key as keyof AppliedFilters]
      }
      return next
    })
    setSelectedRequestIds([])
    setIsMultiSelectEnabled(false)
  }

  const renderInfoRow = (label: string, value: string, numberOfLines = 1): React.ReactNode => (
    <View style={styles.infoRow}>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text style={styles.infoValue} numberOfLines={numberOfLines}>
        {value || '-'}
      </Text>
    </View>
  )

  const getApproverName = (item: DemoMaterialRequest, level: RoleType) =>
    item.approvers?.find(approver => approver.level === level)?.approver_name ||
    t('demo_not_available')

  const getApproverStatusText = (item: DemoMaterialRequest, level: RoleType) => {
    const approverStatus = (
      item.approvers?.find(approver => approver.level === level)?.status || ''
    ).toUpperCase()
    if (approverStatus.includes('APPROVED')) return t('demo_approver_status_approved')
    if (approverStatus.includes('REJECTED')) return t('demo_approver_status_rejected')
    if (approverStatus.includes('PENDING')) return t('demo_approver_status_pending')
    return t('demo_not_available')
  }

  const getPartnerId = (item: DemoMaterialRequest) => item.vendor?.partner_id || '-'

  const getRejectionReason = (item: DemoMaterialRequest) => {
    const rejectedApproverRemark =
      item.approvers?.find(
        approver =>
          [STATUS.REJECTED, STATUS.L1_REJECTED, STATUS.L2_REJECTED].includes(
            (approver.status || '').toUpperCase(),
          ) &&
          Boolean(approver.remarks?.trim()),
      )?.remarks || ''
    const anyApproverRemark =
      item.approvers?.find(approver => Boolean(approver.remarks?.trim()))?.remarks || ''

    return rejectedApproverRemark || anyApproverRemark || '-'
  }

  const getDisplayStatus = (latestStatus?: string) => {
    const status = (latestStatus || '').toUpperCase()
    const l1ViewStatus =
      roleType === RoleType.L1 &&
      [STATUS.L2_PENDING, STATUS.L2_APPROVED, STATUS.L2_REJECTED].includes(status)
        ? STATUS.L1_APPROVED
        : status
    switch (l1ViewStatus) {
      case STATUS.PENDING:
      case STATUS.L1_PENDING:
      case STATUS.L2_PENDING:
        return t('demo_status_pending')
      case STATUS.APPROVED:
      case STATUS.L1_APPROVED:
      case STATUS.L2_APPROVED:
        return t('demo_status_approved')
      case STATUS.REJECTED:
      case STATUS.L1_REJECTED:
      case STATUS.L2_REJECTED:
        return t('demo_status_rejected')
      default:
        return t('demo_status_unknown')
    }
  }

  const getStatusPillStyle = (latestStatus?: string) => {
    const status = (latestStatus || '').toUpperCase()
    const l1ViewStatus =
      roleType === RoleType.L1 &&
      [STATUS.L2_PENDING, STATUS.L2_APPROVED, STATUS.L2_REJECTED].includes(status)
        ? STATUS.L1_APPROVED
        : status
    if ([STATUS.APPROVED, STATUS.L1_APPROVED, STATUS.L2_APPROVED].includes(l1ViewStatus))
      return styles.statusPillApproved
    if ([STATUS.REJECTED, STATUS.L1_REJECTED, STATUS.L2_REJECTED].includes(l1ViewStatus))
      return styles.statusPillRejected
    if (![STATUS.PENDING, STATUS.L1_PENDING, STATUS.L2_PENDING].includes(l1ViewStatus))
      return styles.statusPillUnknown
    return styles.statusPillPending
  }

  const getStatusTextStyle = (latestStatus?: string) => {
    const status = (latestStatus || '').toUpperCase()
    const l1ViewStatus =
      roleType === RoleType.L1 &&
      [STATUS.L2_PENDING, STATUS.L2_APPROVED, STATUS.L2_REJECTED].includes(status)
        ? STATUS.L1_APPROVED
        : status
    if ([STATUS.APPROVED, STATUS.L1_APPROVED, STATUS.L2_APPROVED].includes(l1ViewStatus))
      return styles.statusPillTextApproved
    if ([STATUS.REJECTED, STATUS.L1_REJECTED, STATUS.L2_REJECTED].includes(l1ViewStatus))
      return styles.statusPillTextRejected
    if (![STATUS.PENDING, STATUS.L1_PENDING, STATUS.L2_PENDING].includes(l1ViewStatus))
      return styles.statusPillTextUnknown
    return styles.statusPillTextPending
  }

  const canTakeApproverAction = useCallback(
    (item: DemoMaterialRequest) => {
      const latestStatus = (item.latest_status || '').toUpperCase()
      if (roleType === RoleType.L1) return latestStatus === STATUS.L1_PENDING
      if (roleType === RoleType.L2) return latestStatus === STATUS.L2_PENDING
      return false
    },
    [roleType],
  )

  const canWithdrawRequest = (item: DemoMaterialRequest) => {
    if (!item?.requested_by?.id || !currentUserId) return false
    const status = (item.latest_status || '').toUpperCase()
    const isPending = [STATUS.PENDING, STATUS.L1_PENDING, STATUS.L2_PENDING].includes(status)
    return isPending && item.requested_by.id === currentUserId
  }

  const handleWithdrawRequest = async (requestId: number) => {
    if (actionLoading) return
    try {
      setActionLoading(true)
      await withdrawDemoMaterialRequest(requestId)
      ToastAndroid.show(t('demo_withdraw_success'), ToastAndroid.SHORT)
      setShowDetailsSheet(false)
      setSelectedRequest(null)
      await getRequest()
    } catch {
      ToastAndroid.show(t('demo_withdraw_failed'), ToastAndroid.SHORT)
    } finally {
      setActionLoading(false)
    }
  }

  const selectableRequestIds = useMemo(
    () => request.filter(canTakeApproverAction).map(item => item.id),
    [request, canTakeApproverAction],
  )

  const isSelectAllActive =
    selectableRequestIds.length > 0 &&
    selectedRequestIds.length === selectableRequestIds.length

  const parseActionResultMessage = (
    action: RequestAction,
    response: DemoMaterialActionResult[],
  ) => {
    if (!response?.length) {
      return action === RequestAction.APPROVE
        ? t('demo_action_completed')
        : t('demo_reject_action_completed')
    }
    const approvedCount = response.filter(r =>
      [STATUS.APPROVED, STATUS.L1_APPROVED, STATUS.L2_APPROVED].includes(
        (r.latest_status || '').toUpperCase(),
      ),
    ).length
    const rejectedCount = response.filter(r =>
      [STATUS.REJECTED, STATUS.L1_REJECTED, STATUS.L2_REJECTED].includes(
        (r.latest_status || '').toUpperCase(),
      ),
    ).length
    const unknownCount = response.length - approvedCount - rejectedCount
    const parts = []
    if (approvedCount) parts.push(t('demo_result_count_approved', { count: approvedCount }))
    if (rejectedCount) parts.push(t('demo_result_count_rejected', { count: rejectedCount }))
    if (unknownCount) parts.push(t('demo_result_count_updated', { count: unknownCount }))
    return parts.join(', ')
  }

  const openRejectFor = (isFromDetail: boolean) => {
    setIsDetailAction(isFromDetail)
    setRejectValidationError('')
    setOtherReason('')
    setShowRejectDrawer(true)
  }

  const triggerApprove = (isFromDetail: boolean) => {
    setPendingAction(RequestAction.APPROVE)
    setIsDetailAction(isFromDetail)
    setShowConfirmModal(true)
  }

  const toggleSelect = (id: number) => {
    setSelectedRequestIds(prev =>
      prev.includes(id) ? prev.filter(existingId => existingId !== id) : [...prev, id],
    )
  }

  const toggleSelectAll = () => {
    if (isSelectAllActive) {
      setSelectedRequestIds([])
      return
    }
    setSelectedRequestIds(selectableRequestIds)
  }

  const enforceCharacterLimit = (value: string, maxLength = 100) => {
    if (value.length <= maxLength) return value
    return value.slice(0, maxLength)
  }

  const refreshAfterListAck = async () => {
    setShowListResultPopup(false)
    setSelectedRequestIds([])
    setIsMultiSelectEnabled(false)
    await getRequest()
  }

  const performAction = async (
    action: RequestAction,
    ids: number[],
    reasonPayload?: { reason_config_id: number | null; reason: string },
  ) => {
    if (!ids.length) return false
    try {
      setActionLoading(true)
      const response = await validateDemoMaterialRequest(
        action,
        ids,
        roleType,
        reasonPayload,
      )
      const resultMessage = parseActionResultMessage(action, response || [])
      if (isDetailAction) {
        ToastAndroid.show(resultMessage, ToastAndroid.SHORT)
        setShowDetailsSheet(false)
        setSelectedRequest(null)
        await getRequest()
      } else {
        setListResultMessage(resultMessage)
        setShowListResultPopup(true)
      }
      return true
    } catch {
      ToastAndroid.show(t('unknown_error'), ToastAndroid.SHORT)
      return false
    } finally {
      setActionLoading(false)
      setShowConfirmModal(false)
    }
  }

  const handleConfirmApprove = async () => {
    const ids =
      isDetailAction && selectedRequest
        ? [selectedRequest.id]
        : selectedRequestIds.length > 0
        ? selectedRequestIds
        : selectedRequest
        ? [selectedRequest.id]
        : []
    if (!ids.length) {
      ToastAndroid.show(t('demo_no_request_selected'), ToastAndroid.SHORT)
      return
    }
    await performAction(RequestAction.APPROVE, ids)
  }

  const handleRejectSubmit = async () => {
    if (actionLoading) return
    if (!otherReason.trim()) {
      setRejectValidationError(t('demo_reject_reason_required'))
      return
    }
    setRejectValidationError('')
    const reasonPayload = { reason_config_id: null, reason: otherReason.trim() }

    const ids =
      isDetailAction && selectedRequest
        ? [selectedRequest.id]
        : selectedRequestIds.length > 0
        ? selectedRequestIds
        : selectedRequest
        ? [selectedRequest.id]
        : []
    if (!ids.length) {
      ToastAndroid.show(t('demo_no_request_selected'), ToastAndroid.SHORT)
      return
    }
    const didSucceed = await performAction(RequestAction.REJECT, ids, reasonPayload)
    if (didSucceed) {
      setShowRejectDrawer(false)
      setOtherReason('')
      setRejectValidationError('')
    }
  }

  const renderItem = ({ item }: { item: DemoMaterialRequest }) => (
    <DemoMaterialRequestCard
      item={item}
      styles={styles}
      isMultiSelectEnabled={isMultiSelectEnabled}
      selectedRequestIds={selectedRequestIds}
      currentUserId={currentUserId}
      roleType={roleType}
      canTakeApproverAction={canTakeApproverAction}
      toggleSelect={toggleSelect}
      onOpenRequest={(requestItem: DemoMaterialRequest) => {
        setSelectedRequest(requestItem)
        setShowDetailsSheet(true)
      }}
      onRejectRequest={(requestItem: DemoMaterialRequest) => {
        setSelectedRequest(requestItem)
        setSelectedRequestIds([requestItem.id])
        openRejectFor(false)
      }}
      onApproveRequest={(requestItem: DemoMaterialRequest) => {
        setSelectedRequest(requestItem)
        setSelectedRequestIds([requestItem.id])
        triggerApprove(false)
      }}
      getDisplayStatus={getDisplayStatus}
      getStatusPillStyle={getStatusPillStyle}
      getStatusTextStyle={getStatusTextStyle}
    />
  )

  return (
    <View style={COMMON_STYLES.flex1}>
      {(loading || roleLoading) && <Spinner type="on-screen" />}

      <DemoMaterialRequestFilterHeader
        styles={styles}
        statusFilters={STATUS_FILTERS}
        statusFilter={statusFilter}
        appliedFilters={appliedFilters}
        roleType={roleType}
        selectableRequestIds={selectableRequestIds}
        isMultiSelectEnabled={isMultiSelectEnabled}
        isSelectAllActive={isSelectAllActive}
        onOpenFilter={() => setShowFilterModal(true)}
        onChangeStatus={onChangeStatus}
        onToggleSelectAll={toggleSelectAll}
        onToggleMultiSelect={value => {
          setIsMultiSelectEnabled(value)
          if (!value) {
            setSelectedRequestIds([])
          }
        }}
        onRemoveFilter={handleRemoveFilter}
      />

      {showNoRole && (
        <View style={styles.emptyState}>
          <Text style={[TYPOGRAPHY.HEADING3, TEXT_STYLES.textNeutral80]}>
            {t('demo_no_role_available')}
          </Text>
        </View>
      )}

      {!showNoRole && (
        <FlatList
          data={request}
          keyExtractor={item => String(item.id)}
          renderItem={renderItem}
          onEndReached={fetchNext}
          onEndReachedThreshold={0.6}
          ListEmptyComponent={
            !loading ? (
              <View style={styles.emptyState}>
                <Text style={[TYPOGRAPHY.HEADING3, TEXT_STYLES.textNeutral70]}>
                  {t('demo_no_requests_found')}
                </Text>
              </View>
            ) : null
          }
          ListFooterComponent={loadingNext ? <Spinner type="in-screen" /> : null}
          contentContainerStyle={mediaStyles({
            desktopStyle: styles.listDesktop,
            mobileStyle: styles.listMobile,
          })}
        />
      )}

      {showFilterModal && (
        <FilterModal
          appliedFilterData={appliedFilters}
          onApplyFilters={handleApplyFilters}
          onClose={() => setShowFilterModal(false)}
          filterPage="change_request"
        />
      )}

      {showRoleSelection && (
        <RoleSelection
          visible={showRoleSelection}
          roles={availableRoles}
          onSelect={handleRoleSelect}
        />
      )}
      {roleType === 'REQUESTER' && !showNoRole && (
        <View style={styles.ctaWrap}>
          <View style={styles.ctaInner}>
            <Button
              text={t('demo_new_request')}
              size={SIZE.large}
              type={BUTTON_TYPE.PRIMARY}
              onClick={() => rnNavigation({ name: ROUTES.DEMO_PRODUCT_SELECTION })}
            />
          </View>
        </View>
      )}
      {showDetailsSheet && selectedRequest && (
        <DemoMaterialRequestDetailSheet
          visible={showDetailsSheet}
          selectedRequest={selectedRequest}
          roleType={roleType}
          styles={styles}
          actionLoading={actionLoading}
          isDetailAction={isDetailAction}
          showConfirmModal={showConfirmModal}
          pendingAction={pendingAction}
          canTakeApproverAction={canTakeApproverAction}
          getApproverName={getApproverName}
          getApproverStatusText={getApproverStatusText}
          canWithdrawRequest={canWithdrawRequest}
          getDisplayStatus={getDisplayStatus}
          getStatusPillStyle={getStatusPillStyle}
          getStatusTextStyle={getStatusTextStyle}
          getPartnerId={getPartnerId}
          getRejectionReason={getRejectionReason}
          renderInfoRow={renderInfoRow}
          onClose={() => {
            if (actionLoading) return
            setShowDetailsSheet(false)
          }}
          onOpenReject={() => openRejectFor(true)}
          onTriggerApprove={() => triggerApprove(true)}
          onSetSelectedRequestIds={setSelectedRequestIds}
          onWithdrawRequest={handleWithdrawRequest}
          onCloseConfirm={() => setShowConfirmModal(false)}
          onConfirmApprove={handleConfirmApprove}
        />
      )}
      <DemoMaterialRequestActionsPanel
        styles={styles}
        actionLoading={actionLoading}
        isMultiSelectEnabled={isMultiSelectEnabled}
        statusFilter={statusFilter}
        pendingStatusFilter={STATUS_FILTERS[0]}
        roleType={roleType}
        selectedRequestIds={selectedRequestIds}
        pendingAction={isDetailAction ? null : pendingAction}
        showConfirmModal={!isDetailAction && showConfirmModal}
        showListResultPopup={showListResultPopup}
        listResultMessage={listResultMessage}
        showRejectDrawer={showRejectDrawer}
        otherReason={otherReason}
        rejectValidationError={rejectValidationError}
        onOpenReject={() => openRejectFor(false)}
        onTriggerApprove={() => triggerApprove(false)}
        onCloseConfirmModal={() => setShowConfirmModal(false)}
        onConfirmApprove={handleConfirmApprove}
        onCloseListResult={refreshAfterListAck}
        onCloseRejectDrawer={() => {
          if (actionLoading) return
          setShowRejectDrawer(false)
          setOtherReason('')
          setRejectValidationError('')
        }}
        onSetOtherReason={setOtherReason}
        onSetRejectValidationError={setRejectValidationError}
        onRejectSubmit={handleRejectSubmit}
        enforceCharacterLimit={enforceCharacterLimit}
      />
    </View>
  )
}

export default HomeDemoMaterialRequest
